<template>
    <div>
        <v-radio-group v-model="selectedMethod" v-if="paymentSession" hide-details class="mt-0 pt-0">
            <template
                v-for="paymentMethod in paymentSession.payment_method_categories"
            >
                <v-radio
                    :key="`${paymentMethod.identifier}-radio`"
                    :label="$language.translate(paymentMethod.name)"
                    :value="paymentMethod.identifier"
                ></v-radio>
                <div
                    :id="`klarna-payments-container-${paymentMethod.identifier}`"
                    :key="paymentMethod.identifier" 
                />
                <v-btn
                    :key="`${paymentMethod.identifier}-button`"
                    v-if="displayPayButton && paymentMethod.identifier === selectedMethod"
                    @click="onPayClick"
                    :loading="payLoading"
                    color="primary"
                >
                    {{'Pay with Klarna' | translation}}
                </v-btn>
            </template>
        </v-radio-group>
    </div>
</template>

<script>
    import {KlarnaPaymentType} from './config/klarna-payment-type-config';
    import {mapMutations} from 'vuex';
    import Vue from 'vue';
    import gql from 'graphql-tag';
    import AbstractAggregatorPaymentType from '@breakmedia/prime-vue-payment/src/AbstractAggregatorPaymentType';

    const moduleName = 'KlarnaPaymentType';

    export default {
        name: moduleName,
        extends: AbstractAggregatorPaymentType,
        data() {
            return {
                displayPayButton: false,
                payLoading: false,
                paymentSession: null,
            };
        },
        created() {
            if (this.$route.query.success && this.$route.query.transactionId) {
                this.$emit('success', {transaction_id: this.$route.query.transactionId});
            } else if (!document.getElementById('klarnaPayWidgetJs')) {
                let script = document.createElement('script');
                script.src = 'https://x.klarnacdn.net/kp/lib/v1/api.js';
                script.setAttribute('async', 'async');
                script.id = 'klarnaPayWidgetJs';
                document.getElementsByTagName('script')[0].parentNode.insertBefore(script, document.getElementsByTagName('script')[0]);

                window.klarnaAsyncCallback = this.klarnaAsyncCallback;
            } else {
                this.klarnaAsyncCallback();
            }
        },
        computed: {
            getLastBasketChange() {
                return this.$basket.getLastBasketChange();
            },
            didActualPricesLoaded() {
                return this.$basket.didActualPricesLoaded();
            },
        },
        methods: {
            ...mapMutations('Payment', [
                'setError',
            ]),
            getLocale() {
                const language = this.$language.getLanguage();
                const country = this.$country.getCountry()
                const locale = `${language}-${country}`;
                return locale;
            },
            klarnaAsyncCallback() {
                this.$order.getOrder(true).then(order => {
                    if (order && order.id) {
                        Vue.$graphql
                            .mutate({
                                mutation: gql`
                                    mutation($orderId: String!, $transactionId: String!, $locale: String!) {
                                        klarnaCreatePaymentSession(
                                            orderId: $orderId,
                                            transactionId: $transactionId,
                                            locale: $locale
                                        ) {
                                            client_token
                                            payment_method_categories
                                            error_code
                                            error_messages
                                        }
                                    }
                                `,
                                variables: {
                                    orderId: order.id,
                                    transactionId: order.transaction_id,
                                    locale: this.getLocale(),
                                },
                            })
                            .then(r => r.data.klarnaCreatePaymentSession)
                            .then(paymentSession => {
                                this.paymentSession = paymentSession;
                                Klarna.Payments.init({
                                    client_token: paymentSession.client_token,
                                });
                            })
                            .catch(e => {
                                this.setError(e.toString());
                            });
                    } else {
                        this.setError('Order not created, please try again later.');
                    }
                });
            },
            onPayClick() {
                this.payLoading = true;

                Klarna.Payments.authorize(
                    {
                        payment_method_category: this.selectedMethod,
                    },
                    {},
                    res => {
                        if (!res.approved) {
                            if (res.error && res.error.invalid_fields) {
                                console.log(res.error.invalid_fields);
                            }
                            this.setError('Klarna did not approve the payment. Please contact the support.');
                            this.payLoading = false;
                        } else {
                            this.$order.getOrder().then(order => {
                                if (order && order.id) {
                                    return Vue.$graphql
                                        .mutate({
                                            mutation: gql`
                                                mutation(
                                                    $authorizationToken: String!,
                                                    $orderId: String!,
                                                    $transactionId: String!,
                                                    $locale: String!
                                                ) {
                                                    klarnaPlaceOrder(
                                                        authorizationToken: $authorizationToken,
                                                        orderId: $orderId,
                                                        transactionId: $transactionId,
                                                        locale: $locale
                                                    ) {
                                                        redirect_url
                                                        fraud_status
                                                        error_code
                                                        error_messages
                                                    }
                                                }
                                            `,
                                            variables: {
                                                authorizationToken: res.authorization_token,
                                                orderId: order.id,
                                                transactionId: order.transaction_id,
                                                locale: this.getLocale(),
                                            },
                                        })
                                        .then(r => r.data.klarnaPlaceOrder)
                                        .then(placeOrderResponse => {
                                            if (placeOrderResponse.redirect_url) {
                                                window.location.href = placeOrderResponse.redirect_url;
                                            }
                                        })
                                        .catch(e => {
                                            this.setError(e.toString());
                                            this.payLoading = false;
                                        });
                                } else {
                                    this.setError('Order not created, please try again later.');
                                    this.payLoading = false;
                                }
                            });
                        }
                    }
                );
            },
        },
        watch: {
            getLastBasketChange() {
                if (this.didActualPricesLoaded) {
                    this.klarnaAsyncCallback();
                }
            },
            selectedMethod(value) {
                for (const paymentMethod of this.paymentSession.payment_method_categories) {
                    document.getElementById(`klarna-payments-container-${paymentMethod.identifier}`).innerHTML = '';
                }
                if (value) {
                    Klarna.Payments.load(
                        {
                            container: `#klarna-payments-container-${value}`,
                            payment_method_category: value,
                        },
                        res => {
                            this.displayPayButton = res.show_form;
                        }
                    );
                } else {
                    this.displayPayButton = false;
                }
            },
        },
        install(Vue) {
            Vue.component(moduleName, this);
            Vue.$store.commit('Payment/addPaymentType', new KlarnaPaymentType());
        },
    };
</script>